<template>
  <div class="coverflow-overlay" :style="overlayBgStyle">
    <div class="closeButtons">
      <q-btn icon="close" flat round dense @click="$emit('closeCoverflow')" />
      <ReturnHomeButton @click="$emit('closeAllDialogs')" />
    </div>

    <div class="coverflow-content">
      <div v-if="loading" class="state-panel">Chargement...</div>
      <div v-else-if="resources.length === 0" class="state-panel">Aucune ressource à afficher</div>

      <Swiper
        v-else
        class="coverflow-swiper"
        :modules="modules"
        effect="coverflow"
        :grab-cursor="true"
        :centered-slides="true"
        slides-per-view="auto"
        :loop="resources.length >= 3"
        :keyboard="{ enabled: true }"
        :navigation="true"
        :pagination="{ clickable: true }"
        :coverflow-effect="{
          rotate: 35,
          depth: 180,
          modifier: 1,
          slideShadows: false,
        }"
        @swiper="onSwiper"
      >
        <SwiperSlide v-for="(resource, index) in resources" :key="index" class="coverflow-slide">
          <div class="slide-card" :class="{ 'is-active': activeIndex === index }">
            <img
              v-if="resource.type === 'image' && resource.url"
              :src="resource.url"
              :alt="resource.title || resource.name"
              @load="onMediaLoaded"
              @click="onSlideClick(index, resource)"
            />
            <video
              v-else-if="resource.type === 'video' && resource.url"
              :src="resource.url"
              muted
              playsinline
              controls
              @loadedmetadata="onMediaLoaded"
              @click="onSlideClick(index, resource)"
            />
            <div v-else class="file-card" @click="onSlideClick(index, resource)">
              <q-icon :name="fileIcon(resource.type)" size="48px" />
              <span>{{ resource.url === null ? 'Média indisponible' : (resource.title || resource.name || 'Ressource') }}</span>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>

    <!-- Modal de zoom -->
    <div v-if="showZoom" class="zoom-modal" @click="closeZoom">
      <div class="zoom-content" @click.stop>
        <q-btn icon="close" flat round dense class="close-zoom-btn" @click="closeZoom" />
        <img :src="zoomedImage" class="zoomed-image" />
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios';
import { defineEmits, defineProps, onMounted, ref } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue.js';
import { EffectCoverflow, Navigation, Pagination, Keyboard } from 'swiper';
import 'swiper/swiper.min.css';
import 'swiper/modules/effect-coverflow/effect-coverflow.min.css';
import 'swiper/modules/navigation/navigation.min.css';
import 'swiper/modules/pagination/pagination.min.css';
import ReturnHomeButton from './ReturnHomeButton.vue';
import { useOverlayBg } from '../composables/useOverlayBg';

const props = defineProps(['blockDatas']);
defineEmits(['closeCoverflow', 'closeAllDialogs']);

const { overlayBgStyle } = useOverlayBg();

const modules = [EffectCoverflow, Navigation, Pagination, Keyboard];

const resources = ref([]);
const loading = ref(true);
const activeIndex = ref(0);
const showZoom = ref(false);
const zoomedImage = ref('');
const swiperInstance = ref(null);

const onSwiper = (swiper) => {
  swiperInstance.value = swiper;
};

// Les slides ont une largeur qui suit le ratio de chaque image ; il faut donc
// recalculer les positions Swiper une fois que chaque média a ses dimensions.
const onMediaLoaded = () => {
  if (swiperInstance.value) swiperInstance.value.update();
};

const getResourceType = (resource) => {
  const name = resource?.name || '';
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (/\.(mp4|avi|mov|wmv|webm|mkv)$/i.test(name)) return 'video';
  if (/\.(mp3|wav|ogg|m4a)$/i.test(name)) return 'audio';
  if (/\.(pdf|doc|docx)$/i.test(name)) return 'doc';
  return 'file';
};

const fileIcon = (type) => {
  if (type === 'audio') return 'audiotrack';
  if (type === 'doc') return 'description';
  return 'insert_drive_file';
};

const onSlideClick = (index, resource) => {
  if (index !== activeIndex.value) return;
  if (resource.type === 'image' && resource.url) {
    openZoom(resource.url);
  }
};

const openZoom = (imageUrl) => {
  zoomedImage.value = imageUrl;
  showZoom.value = true;
};

const closeZoom = () => {
  showZoom.value = false;
  zoomedImage.value = '';
};

const fetchResources = async () => {
  const associatedResources = props.blockDatas?.associatedResources || [];
  loading.value = true;

  const fetched = await Promise.all(
    associatedResources.map(async (resource, position) => {
      const sortIndex = Number.isFinite(Number(resource.index)) ? Number(resource.index) : position;
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`);
        return {
          ...resource,
          url: response.data,
          type: getResourceType(resource),
          sortIndex,
        };
      } catch (error) {
        return {
          ...resource,
          url: null,
          type: getResourceType(resource),
          sortIndex,
        };
      }
    })
  );

  fetched.sort((a, b) => a.sortIndex - b.sortIndex);
  resources.value = fetched;
  loading.value = false;
};

onMounted(fetchResources);
</script>

<style scoped>
.coverflow-overlay {
  background-color: rgba(217, 217, 217, 1);
  position: fixed;
  inset: 0;
  z-index: 9999;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: overlayFadeIn 200ms ease both;

  --swiper-navigation-color: #1f1f1f;
  --swiper-pagination-color: #1f1f1f;
  --swiper-pagination-bullet-inactive-color: #1f1f1f;
  --swiper-pagination-bullet-inactive-opacity: 0.35;
}

.closeButtons {
  position: absolute;
  top: 16px;
  right: 16px;
  width: auto;
  margin: 0;
  display: flex;
  gap: 10px;
  z-index: 10001;
}

.coverflow-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 76px clamp(12px, 3vw, 48px) 56px;
}

.state-panel {
  color: #1f1f1f;
  font-size: 1.2rem;
}

.coverflow-swiper {
  width: 100%;
  height: 100%;
}

.coverflow-slide {
  width: auto;
  max-width: 80vw;
  height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-card {
  width: auto;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 38px rgba(0, 0, 0, 0.28);
  background: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-card img,
.slide-card video {
  width: auto;
  height: 100%;
  max-width: 80vw;
  object-fit: contain;
  border-radius: 8px;
  user-select: none;
  -webkit-user-drag: none;
}

.slide-card.is-active img,
.slide-card.is-active video {
  cursor: pointer;
}

.file-card {
  width: min(420px, 70vw);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #1f1f1f;
  padding: 24px;
  text-align: center;
  cursor: pointer;
}

/* Styles pour la modal de zoom */
.zoom-modal {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  cursor: pointer;
  padding: 24px;
}

.zoom-content {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: default;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoomed-image {
  display: block;
  width: auto;
  height: auto;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 48px);
  min-width: 0;
  min-height: 0;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.close-zoom-btn {
  position: fixed;
  top: 16px;
  right: 16px;
  background-color: rgba(255, 255, 255, 0.9);
  color: #333;
  z-index: 10001;
}

@keyframes overlayFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 900px) {
  .coverflow-content {
    padding: 70px 12px 48px;
  }

  .coverflow-slide {
    max-width: 82vw;
    height: 54vh;
  }
}

@media (max-width: 640px) {
  .coverflow-content {
    padding: 64px 8px 40px;
  }

  .coverflow-slide {
    max-width: 86vw;
    height: 48vh;
  }
}
</style>
